import React from "react";

type ProfessionalGroup<T> = {
  idProfissional: number;
  nome: string;
  items: T[];
};

type SpecialtyGroup<T> = {
  especialidade: string;
  profissionais: ProfessionalGroup<T>[];
};

type Density = "compact" | "comfortable";

type GroupedProfessionalsAccordionProps<T> = {
  groups: SpecialtyGroup<T>[];
  expandedEspecialidades: string[];
  expandedProfissionais: number[];
  currentPages: Record<number, number>;
  itemsPerPage: number;
  density?: Density;
  onToggleEspecialidade: (especialidade: string) => void;
  onToggleProfissional: (idProfissional: number, especialidade: string) => void;
  onPageChange: (idProfissional: number, page: number) => void;
  renderItem: (item: T) => React.ReactNode;
};

const densityClasses: Record<
  Density,
  {
    groupCard: string;
    specialtyTitle: string;
    professionalCard: string;
    professionalTitle: string;
    itemsList: string;
    pagination: string;
    paginationButton: string;
    paginationText: string;
  }
> = {
  compact: {
    groupCard: "border rounded-lg p-2",
    specialtyTitle: "text-xs font-bold text-black-700 uppercase",
    professionalCard: "ml-3 border-l-2 border-gray-300 pl-2",
    professionalTitle: "font-medium text-xs text-gray-500",
    itemsList: "mt-1 space-y-1",
    pagination: "flex justify-center items-center mt-2 gap-1",
    paginationButton: "px-2 py-1 bg-gray-200 rounded text-xs disabled:opacity-50",
    paginationText: "text-xs text-gray-600",
  },
  comfortable: {
    groupCard: "border rounded-lg p-3",
    specialtyTitle: "text-xs font-bold text-black-700 uppercase",
    professionalCard: "ml-4 border-l-2 border-gray-300 pl-3",
    professionalTitle: "font-medium text-sm text-gray-500",
    itemsList: "mt-2 space-y-2",
    pagination: "flex justify-center items-center mt-4 gap-2",
    paginationButton: "px-3 py-1 bg-gray-200 rounded text-sm disabled:opacity-50",
    paginationText: "text-xs text-gray-600",
  },
};

function GroupedProfessionalsAccordion<T>({
  groups,
  expandedEspecialidades,
  expandedProfissionais,
  currentPages,
  itemsPerPage,
  density = "comfortable",
  onToggleEspecialidade,
  onToggleProfissional,
  onPageChange,
  renderItem,
}: GroupedProfessionalsAccordionProps<T>) {
  const classes = densityClasses[density];

  return (
    <div className="space-y-3">
      {groups.map((grupo) => {
        const especialidadeAberta = expandedEspecialidades.includes(
          grupo.especialidade,
        );

        return (
          <div key={grupo.especialidade} className={classes.groupCard}>
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() => onToggleEspecialidade(grupo.especialidade)}
            >
              <div>
                <h4 className={classes.specialtyTitle}>{grupo.especialidade}</h4>
              </div>
              <span className="text-blue-600 text-xl font-bold">
                {especialidadeAberta ? "-" : "+"}
              </span>
            </div>

            {especialidadeAberta && (
              <div className="mt-3 space-y-4">
                {grupo.profissionais.map((profissional) => {
                  const profissionalAberto = expandedProfissionais.includes(
                    profissional.idProfissional,
                  );
                  const paginaAtual = currentPages[profissional.idProfissional] || 1;
                  const totalPaginas = Math.ceil(
                    profissional.items.length / itemsPerPage,
                  );
                  const inicio = (paginaAtual - 1) * itemsPerPage;
                  const fim = inicio + itemsPerPage;
                  const itemsPaginados = profissional.items.slice(inicio, fim);

                  return (
                    <div
                      key={profissional.idProfissional}
                      className={classes.professionalCard}
                    >
                      <div
                        className="flex justify-between items-center cursor-pointer"
                        onClick={() =>
                          onToggleProfissional(
                            profissional.idProfissional,
                            grupo.especialidade,
                          )
                        }
                      >
                        <h5 className={classes.professionalTitle}>
                          {profissional.nome}
                        </h5>
                        <span className="text-blue-600 text-xl font-bold">
                          {profissionalAberto ? "-" : "+"}
                        </span>
                      </div>

                      {profissionalAberto && (
                        <div className={classes.itemsList}>
                          {itemsPaginados.map(renderItem)}

                          {profissional.items.length > itemsPerPage && (
                            <div className={classes.pagination}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onPageChange(
                                    profissional.idProfissional,
                                    Math.max(paginaAtual - 1, 1),
                                  );
                                }}
                                disabled={paginaAtual === 1}
                                className={classes.paginationButton}
                              >
                                Anterior
                              </button>

                              <span className={classes.paginationText}>
                                Pg {paginaAtual}/{totalPaginas}
                              </span>

                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onPageChange(
                                    profissional.idProfissional,
                                    Math.min(paginaAtual + 1, totalPaginas),
                                  );
                                }}
                                disabled={paginaAtual === totalPaginas}
                                className={classes.paginationButton}
                              >
                                Próxima
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default GroupedProfessionalsAccordion;

